import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/authService'
import { useNavigationProgress } from '@/composables/useNavigationProgress'

const ROUTE_IMPORT_RELOAD_PREFIX = 'relay.route-import-reload:'

function isDynamicImportFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  )
}

/**
 * Shared lazy loaders.
 *
 * `teams/:id` and `teams/:id/work/:workId` both render TeamPageView in the
 * default outlet. Vue reuses a mounted component only when the vnode type is
 * identical, so both records must point at the same loader — otherwise opening
 * an epic would tear the board down, which is the whole thing we are avoiding.
 */
const TeamPageView = () => import('@/views/TeamPageView.vue')
const WorkspaceView = () => import('@/views/WorkspaceView.vue')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true }
    },
    {
      path: '/auth-callback',
      name: 'auth-callback',
      component: () => import('@/views/AuthCallbackView.vue'),
      meta: { public: true }
    },
    {
      // The shell owns the chrome — sidebar, mobile bar, keep-alive — so it is
      // not rebuilt on every navigation. Child paths are relative to '/'.
      path: '/',
      component: () => import('@/views/AppShell.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard-teams',
          alias: ['dashboard'],
          component: () => import('@/views/DashboardView.vue'),
          meta: { requiresAuth: true, federationTab: 'teams' }
        },
        {
          path: 'products',
          name: 'dashboard-products',
          component: () => import('@/views/DashboardView.vue'),
          meta: { requiresAuth: true, federationTab: 'products' }
        },
        {
          path: 'signals',
          name: 'meta-signals',
          component: () => import('@/views/MetaSignalsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'teams/:id',
          name: 'team',
          component: TeamPageView,
          meta: { requiresAuth: true }
        },
        {
          // The epic over its board. Same TeamPageView in the default outlet, so
          // the board stays mounted with its scroll, filters and drag state; the
          // epic renders into the shell's `overlay` outlet on top of it.
          path: 'teams/:id/work/:workId',
          name: 'team-work',
          components: { default: TeamPageView, overlay: WorkspaceView },
          meta: { requiresAuth: true }
        },
        {
          path: 'products/:id',
          name: 'product',
          component: () => import('@/views/ProductView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'teams/:teamId/efforts/:effortId',
          name: 'effort',
          component: () => import('@/views/EffortView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'products/:productId/efforts/:effortId',
          name: 'effort-by-product',
          component: () => import('@/views/EffortView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'products/:id/support',
          name: 'support-chat',
          component: () => import('@/views/SupportChatView.vue'),
          meta: { requiresAuth: true }
        },
        {
          // Cold entry: no board to sit over, so the epic renders as a full page.
          path: 'work/:id',
          name: 'work',
          component: WorkspaceView,
          meta: { requiresAuth: true }
        },
        {
          path: 'features/:id',
          name: 'workspace',
          component: WorkspaceView,
          meta: { requiresAuth: true }
        },
        {
          path: 'settings',
          component: () => import('@/views/SettingsView.vue'),
          meta: { requiresAuth: true },
          redirect: '/settings/integrations',
          children: [
            {
              path: 'account',
              name: 'settings-account',
              component: () => import('@/views/settings/AccountSettingsView.vue')
            },
            {
              path: 'integrations',
              name: 'settings',
              component: () => import('@/views/settings/IntegrationsSettingsView.vue')
            },
            {
              path: 'products',
              name: 'settings-products',
              component: () => import('@/views/settings/ProductsSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'] }
            },
            {
              path: 'domains',
              name: 'settings-domains',
              component: () => import('@/views/settings/DomainsSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'] }
            },
            {
              path: 'teams',
              name: 'settings-teams',
              component: () => import('@/views/settings/TeamsSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'] }
            },
            {
              // Its own page rather than a section in the team drawer: the mapping grid needs the
              // full settings column, which a drawer cannot give it.
              //
              // Not admin-gated: team members read the mapping and run one-time sync here, as they
              // could in the board drawer this replaced. The page is read-only for them, and the
              // server gates reads on team membership and writes on ROLE_ADMIN.
              path: 'teams/:teamId/status-mapping',
              name: 'settings-team-status-mapping',
              component: () => import('@/views/settings/StatusMappingSettingsView.vue'),
            },
            {
              path: 'users',
              name: 'settings-users',
              component: () => import('@/views/settings/UsersSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'] }
            },
            {
              path: 'customers',
              name: 'settings-customers',
              component: () => import('@/views/settings/CustomersSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'] }
            },
            {
              path: 'signal-sources',
              name: 'settings-signal-sources',
              component: () => import('@/views/settings/SignalSourcesSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'] }
            },
            {
              path: 'knowledge-bases',
              name: 'settings-knowledge-bases',
              component: () => import('@/views/settings/KnowledgeBasesSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'] }
            },
            {
              // Gated on a server-side allowlist as well as ROLE_ADMIN. `requiredRoles` alone would be
              // the wrong bar — any admin can grant themselves ROLE_ADMIN — so the capability flag the
              // backend puts on the user is what decides. The flag cannot ride on a JWT claim, so the
              // guard resolves it against /api/auth/me when it is not yet known.
              path: 'usage',
              name: 'settings-usage',
              component: () => import('@/views/settings/UsageSettingsView.vue'),
              meta: { requiredRoles: ['ROLE_ADMIN'], requiresAiUsageAccess: true }
            },
            {
              // MCP setup now lives inside the Integrations tab; keep the old path
              // working for bookmarks/links.
              path: 'mcp',
              name: 'settings-mcp',
              redirect: '/settings/integrations'
            }
          ]
        }
      ]
    },
    {
      path: '/clusters',
      redirect: { name: 'meta-signals', query: { view: 'clusters' } },
      meta: { requiresAuth: true }
    },
    {
      path: '/explore',
      redirect: { name: 'meta-signals', query: { view: 'explore' } },
      meta: { requiresAuth: true }
    },
    {
      path: '/domains/studio/new',
      name: 'settings-domains-studio-new',
      component: () => import('@/views/settings/DomainStudioView.vue'),
      meta: { requiresAuth: true, requiredRoles: ['ROLE_ADMIN'] }
    },
    {
      path: '/domains/studio/:id',
      name: 'settings-domains-studio',
      component: () => import('@/views/settings/DomainStudioView.vue'),
      meta: { requiresAuth: true, requiredRoles: ['ROLE_ADMIN'] }
    },
    {
      path: '/authorize',
      name: 'mcp-oauth-authorize-compat',
      beforeEnter: (to) => {
        const queryStart = to.fullPath.indexOf('?')
        const query = queryStart >= 0 ? to.fullPath.slice(queryStart) : ''
        window.location.assign(`/api/oauth/authorize${query}`)
        return false
      },
      component: { template: '' },
      meta: { public: true }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { public: true }
    }
  ]
})

const navigationProgress = useNavigationProgress()

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  if (to.path !== _from.path) {
    navigationProgress.start()
  }

  const authStore = useAuthStore()
  const hasToken = authService.hasToken()

  // If route is public, allow access
  if (to.meta.public) {
    // If already authenticated and going to login, redirect to dashboard
    if (hasToken && to.path === '/login') {
      return next('/')
    }
    return next()
  }

  // Route requires auth
  if (!hasToken) {
    // Store intended destination for redirect after login
    sessionStorage.setItem('auth_redirect', to.fullPath)
    return next('/login')
  }

  // Check token expiration
  let refreshedThisNavigation = false
  if (authService.isTokenExpired()) {
    // Try to refresh
    const refreshed = await authStore.refreshToken()
    if (!refreshed) {
      // Clear the stale token so the login page doesn't redirect back here
      authService.removeToken()
      sessionStorage.setItem('auth_redirect', to.fullPath)
      return next('/login')
    }
    refreshedThisNavigation = true
  }

  // Fetch the user when we don't have one, and also right after a refresh: refreshToken() fills
  // `user` in from the new token's claims, which the JWT alone cannot complete — no hasGithubToken,
  // no defaultProductId, no teams. Without this the `!authStore.user` check below is already
  // satisfied by that partial object, so on any load with an expired access token (they last 24h,
  // refresh tokens 7 days, so this is the normal return visit) the session ran the whole way with
  // no `teams` and GitHub reading as disconnected.
  if (!authStore.user || refreshedThisNavigation) {
    try {
      await authStore.fetchCurrentUser()
    } catch {
      // fetchCurrentUser handles its own errors (including logging out on a
      // confirmed 401/403) - nothing further to do here.
    }
    if (!authStore.user) {
      // fetchCurrentUser only clears the token when the server actually
      // rejected it. If a token is still present, this was a transient
      // failure (slow/unavailable backend) - don't force a login loop over it.
      if (!authService.hasToken()) {
        sessionStorage.setItem('auth_redirect', to.fullPath)
        return next('/login')
      }
      return next(false)
    }
  }

  const { isSupport } = authStore

  // Support users: block access to feature workspace
  if (isSupport && to.name === 'workspace') {
    return next({ name: 'dashboard-products' })
  }

  // Support users: redirect product view to support chat
  if (isSupport && to.name === 'product') {
    return next({ name: 'support-chat', params: { id: to.params.id } })
  }

  // Product pages are no longer a maker-facing surface. Keep the product list for support users.
  if (!isSupport && (to.name === 'dashboard-products' || to.name === 'product')) {
    return next({ name: 'dashboard-teams' })
  }

  // Auto-redirect on initial page load or immediately after login/auth-callback.
  // When a user explicitly navigates home from inside the app (e.g. logo click), honour their intent.
  // Support-only users use the Products list instead of Teams.
  if (
    isSupport &&
    (to.name === 'dashboard-teams' || to.path === '/dashboard' || to.path === '/')
  ) {
    return next({ name: 'dashboard-products' })
  }

  // Check role requirements if specified
  const userRoles = authStore.user?.roles ?? []
  const requiredRoles = Array.isArray(to.meta.requiredRoles)
    ? (to.meta.requiredRoles as string[])
    : []
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) =>
      userRoles.includes(role)
    )
    if (!hasRequiredRole) {
      return next('/')
    }
  }

  // A capability the server grants from a config allowlist, checked on top of any role above.
  // Roles are the wrong bar for AI spend data because admins can grant roles to each other; this
  // flag can only be changed by changing configuration.
  //
  // `undefined` means "not known yet", not "denied". setUserFromToken rebuilds the user from JWT
  // claims, which cannot carry this flag — the state immediately after the SSO callback — so
  // treating absent as denied bounced an allowlisted admin off a deep link to this page. Resolve
  // it against the server once, then decide.
  if (to.meta.requiresAiUsageAccess) {
    if (authStore.user && authStore.user.canViewAiUsage === undefined) {
      try {
        await authStore.fetchCurrentUser()
      } catch {
        // fetchCurrentUser handles its own errors; fall through to the check below.
      }
    }
    if (!authStore.user?.canViewAiUsage) {
      return next('/')
    }
  }

  next()
})

router.afterEach((to) => {
  navigationProgress.done()
  sessionStorage.removeItem(`${ROUTE_IMPORT_RELOAD_PREFIX}${to.fullPath}`)
})

router.onError((error, to) => {
  navigationProgress.done()
  if (!isDynamicImportFailure(error)) return

  const reloadKey = `${ROUTE_IMPORT_RELOAD_PREFIX}${to.fullPath}`
  if (sessionStorage.getItem(reloadKey)) {
    console.error('Route component failed to load after refresh', error)
    return
  }

  sessionStorage.setItem(reloadKey, '1')
  window.location.assign(to.fullPath)
})

export default router
